import type { FC, PropsWithChildren } from "react";

import { Typography } from "@ui/components/Typography";
import { useArticleInfoDisplay } from "@ui/hooks/useArticleInfoDisplay/useArticleInfoDisplay";

import { PictureBehaviour } from "@jti/core-components/components/PictureBehaviour";
import { Column, Row } from "@jti/core-components/components/Row";
import { Section } from "@jti/core-components/components/Section";
import type { BlogItemImage } from "@jti/fe-common";

import styles from "./BlogArticle.module.scss";

export type BlogArticleProps = {
  title: string;
  topicName: string;
  mainImage: BlogItemImage | null;
  creationDate: string;
  readTime?: string;
  showAuthor?: boolean;
  author?: string;
};

export const BlogArticle: FC<PropsWithChildren<BlogArticleProps>> = ({
  title,
  children,
  topicName,
  mainImage,
  creationDate,
  readTime,
  author,
  showAuthor,
}) => {
  const articleInfo = useArticleInfoDisplay(creationDate, readTime);
  return (
    <>
      <Row className={styles.articleRow}>
        <Column md={2}></Column>
        <Column xs={12} md={8}>
          <div className={styles.header}>
            <div className={styles.textWrapper}>
              <div className={styles.title}>{topicName}</div>
              <Typography variant="h1" className={styles.subTitle}>
                {title}
              </Typography>
              <div className={styles.additionalInfo}>
                {showAuthor && author && <div data-testid="author">{author}</div>}
                <div data-testid="creationDate">{articleInfo}</div>
              </div>
            </div>
          </div>
        </Column>
      </Row>
      <Row>
        <Column xs={12} md={12}>
          <div className={styles.imageWrapper}>
            {mainImage && typeof mainImage === "object" && (
              <PictureBehaviour
                sources={[
                  { srcSet: mainImage.smallReference, media: "(max-width: 959px)" },
                  { srcSet: mainImage.mediumReference, media: "(min-width: 960px) and (max-width: 1279px)" },
                ]}
                imageProps={{
                  className: styles.image,
                  src: mainImage.largeReference,
                  alt: mainImage.altText,
                  "data-testid": "blogArticleHeaderImage",
                }}
              />
            )}
            {mainImage && typeof mainImage === "string" && (
              <img data-testid="blogArticleHeaderImage" src={mainImage} className={styles.image} alt="" />
            )}
          </div>
        </Column>
      </Row>
      <Row className={styles.articleContent}>
        <Column md={2}></Column>
        <Column xs={12} md={8}>
          <Section>{children}</Section>
        </Column>
      </Row>
    </>
  );
};
